/**
 * Realtime meetings (PLAN-085) — the binary audio frame the browser sends to
 * the `MeetingRoom` Durable Object over the room socket.
 *
 * Pure, dependency-free and shared by `web/` and `worker/` on purpose: the
 * encoder runs in the browser transcriber, the decoder in the DO, and the
 * only way those two can never disagree is one codec (AGENTS.md rule 2 —
 * `contracts/` is the single place both sides may import).
 *
 * Layout (little-endian, 13-byte header + payload):
 *
 * ```
 * [u8  version = 1]
 * [u32 seq]        monotonically increasing per connection
 * [u32 startMs]    always 0 — see below
 * [u32 endMs]      the segment's DURATION in ms
 * [i16 pcm …]      16 kHz mono PCM16, that many ms of audio
 * ```
 *
 * The header carries a **duration**, not a position. A browser cannot say
 * when a segment happened on the meeting's clock: its segmenter counts audio
 * seconds since it was built, does not advance while muted, and restarts
 * whenever the microphone track is replaced. So the room stamps every
 * accepted segment itself (`segmentTimes` in `worker/meetings/ingest-policy.ts`)
 * and trusts the header only for `endMs - startMs`, which it cross-checks
 * against the PCM it received (REVIEW-2 #1).
 *
 * A 15 s segment is 480 kB — well inside the Durable Object 32 MiB received
 * WebSocket message limit, and inside the ~1 MB Workers AI Whisper request
 * size the CF chunking tutorial demonstrates.
 */

export const AUDIO_FRAME_VERSION = 1

/** Header bytes before the PCM payload. */
export const AUDIO_FRAME_HEADER_BYTES = 13

/** Everything downstream (WAV wrapping, Whisper) assumes this rate. */
export const MEETING_SAMPLE_RATE = 16_000

/** Shorter segments are noise/hallucination fuel — the segmenter drops them. */
export const MIN_SEGMENT_MS = 1_000

/** Longer segments hurt latency and grow the Whisper request. */
export const MAX_SEGMENT_MS = 15_000

export interface AudioFrame {
  seq: number
  startMs: number
  endMs: number
  /** 16 kHz mono PCM16 samples. */
  pcm: Int16Array
}

export class AudioFrameError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AudioFrameError'
  }
}

function assertRange(frame: Omit<AudioFrame, 'pcm'>): void {
  const { seq, startMs, endMs } = frame
  for (const [name, value] of [
    ['seq', seq],
    ['startMs', startMs],
    ['endMs', endMs],
  ] as const) {
    if (!Number.isInteger(value) || value < 0 || value > 0xffff_ffff) {
      throw new AudioFrameError(`${name} must be a uint32, got ${value}`)
    }
  }
  const durationMs = endMs - startMs
  if (durationMs <= 0) {
    throw new AudioFrameError(`endMs must be after startMs (${startMs} → ${endMs})`)
  }
  if (durationMs > MAX_SEGMENT_MS) {
    throw new AudioFrameError(`segment is ${durationMs} ms, max is ${MAX_SEGMENT_MS} ms`)
  }
}

export function encodeAudioFrame(frame: AudioFrame): ArrayBuffer {
  assertRange(frame)
  const buffer = new ArrayBuffer(AUDIO_FRAME_HEADER_BYTES + frame.pcm.byteLength)
  const view = new DataView(buffer)
  view.setUint8(0, AUDIO_FRAME_VERSION)
  view.setUint32(1, frame.seq, true)
  view.setUint32(5, frame.startMs, true)
  view.setUint32(9, frame.endMs, true)
  new Uint8Array(buffer, AUDIO_FRAME_HEADER_BYTES).set(
    new Uint8Array(frame.pcm.buffer, frame.pcm.byteOffset, frame.pcm.byteLength)
  )
  return buffer
}

export function decodeAudioFrame(data: ArrayBuffer): AudioFrame {
  if (data.byteLength < AUDIO_FRAME_HEADER_BYTES) {
    throw new AudioFrameError(`frame is ${data.byteLength} bytes, header alone is 13`)
  }
  const view = new DataView(data)
  const version = view.getUint8(0)
  if (version !== AUDIO_FRAME_VERSION) {
    throw new AudioFrameError(`unsupported frame version ${version}`)
  }
  const payloadBytes = data.byteLength - AUDIO_FRAME_HEADER_BYTES
  if (payloadBytes % 2 !== 0) {
    throw new AudioFrameError(`PCM payload must be a whole number of int16 samples`)
  }
  const frame = {
    seq: view.getUint32(1, true),
    startMs: view.getUint32(5, true),
    endMs: view.getUint32(9, true),
  }
  assertRange(frame)
  // `slice` copies: the header is 13 bytes, so a zero-copy Int16Array view on
  // the payload would be misaligned.
  return { ...frame, pcm: new Int16Array(data.slice(AUDIO_FRAME_HEADER_BYTES)) }
}

/** Samples one segment holds at 16 kHz — the DO cross-checks the declared duration. */
export function samplesForMs(ms: number): number {
  return Math.round((ms * MEETING_SAMPLE_RATE) / 1000)
}

/** Milliseconds a PCM16 buffer represents at 16 kHz. */
export function msForSamples(samples: number): number {
  return Math.round((samples * 1000) / MEETING_SAMPLE_RATE)
}

/**
 * Frame ordering guard. The DO drops a frame whose seq did not advance —
 * that is either a replay or a second tab racing the newest connection.
 */
export function frameSeqOk(lastSeq: number | null, seq: number): boolean {
  return lastSeq === null || seq > lastSeq
}
